import JSZip from "jszip";

import { getAccessToken } from "@/shared/types/auth";

async function getDocumentDownloadUrl(estateId: number, documentId: number): Promise<string> {
  const accessToken = await getAccessToken();
  if (!accessToken) throw new Error("Authentication required");

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}/document/${documentId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
    },
  );

  if (!response.ok) throw new Error("Failed to get document URL");
  const data = await response.json();
  return data.downloadUrl;
}

export async function downloadSingleDocument(
  estateId: number,
  documentId: number,
  fileName: string,
): Promise<void> {
  const downloadUrl = await getDocumentDownloadUrl(estateId, documentId);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function downloadDocumentsAsZip(
  estateId: number,
  docs: { id: number; display_name: string | null; file_name: string }[],
  zipName: string,
): Promise<void> {
  if (docs.length === 0) return;

  if (docs.length === 1) {
    await downloadSingleDocument(estateId, docs[0].id, docs[0].display_name || docs[0].file_name);
    return;
  }

  const zip = new JSZip();
  const usedNames = new Set<string>();

  for (const doc of docs) {
    try {
      const url = await getDocumentDownloadUrl(estateId, doc.id);
      const response = await fetch(url);
      if (!response.ok) continue;
      const blob = await response.blob();

      let name = doc.display_name || doc.file_name;
      if (usedNames.has(name)) {
        const ext = name.lastIndexOf(".");
        const base = ext > 0 ? name.slice(0, ext) : name;
        const suffix = ext > 0 ? name.slice(ext) : "";
        let counter = 2;
        while (usedNames.has(`${base} (${counter})${suffix}`)) counter++;
        name = `${base} (${counter})${suffix}`;
      }
      usedNames.add(name);

      zip.file(name, blob);
    } catch {
      // skip failed downloads
    }
  }

  if (Object.keys(zip.files).length === 0) {
    throw new Error("No files could be downloaded");
  }

  const content = await zip.generateAsync({ type: "blob" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(content);
  link.download = `${zipName}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}
